/**
 * Safety Threshold Service
 * 
 * Metadata-driven safety threshold management for FERP life-critical systems.
 * Replaces hardcoded safety thresholds with tenant-configurable metadata.
 * 
 * FERP Compliance Requirements:
 * - Repository pattern for all data access
 * - Immutable audit trails for threshold access
 * - Life-critical safety preservation (98%+ safety scores)
 * - Multi-tenant threshold isolation
 * - Emergency override capabilities with audit logging
 */

import { useCallback } from 'react';
import { RegulatoryComplianceRepository, MockRegulatoryComplianceRepository } from '../data/repositories/RegulatoryComplianceRepository';
import { useAuth } from '../hooks/useAuth';

// Core threshold types for metadata-driven configuration
export interface SafetyThreshold {
    value: number;
    unit: string;
    framework: string;
    source: 'TENANT_CONFIG' | 'REGULATORY_REQUIREMENT' | 'EMERGENCY_OVERRIDE' | 'SYSTEM_DEFAULT';
    lastUpdated: string;
    updatedBy?: string;
    auditTrail: ThresholdAuditEntry[];
}

export interface ThresholdAuditEntry {
    timestamp: string;
    userId: string;
    action: 'THRESHOLD_ACCESS' | 'THRESHOLD_UPDATE' | 'EMERGENCY_OVERRIDE';
    oldValue?: number;
    newValue: number;
    justification?: string;
    tenantId: string;
}

export interface DomainSafetyThresholds {
    overall: SafetyThreshold;
    healthcare: SafetyThreshold;
    financial: SafetyThreshold;
    legal: SafetyThreshold;
    operational: SafetyThreshold;
    emergency: SafetyThreshold;
}

export interface ThresholdQuery {
    tenantId: string;
    domain?: string;
    frameworkId: string;
    thresholdType: SafetyThresholdType;
}

export enum SafetyThresholdType {
    OVERALL_SAFETY_SCORE = 'OVERALL_SAFETY_SCORE',
    HEALTHCARE_SAFETY_SCORE = 'HEALTHCARE_SAFETY_SCORE', 
    FINANCIAL_SAFETY_SCORE = 'FINANCIAL_SAFETY_SCORE',
    LEGAL_SAFETY_SCORE = 'LEGAL_SAFETY_SCORE',
    OPERATIONAL_SAFETY_SCORE = 'OPERATIONAL_SAFETY_SCORE',
    EMERGENCY_RESPONSE_THRESHOLD = 'EMERGENCY_RESPONSE_THRESHOLD',
    AUTONOMOUS_DECISION_THRESHOLD = 'AUTONOMOUS_DECISION_THRESHOLD',
    CRITICAL_ALERT_THRESHOLD = 'CRITICAL_ALERT_THRESHOLD',
    WARNING_ALERT_THRESHOLD = 'WARNING_ALERT_THRESHOLD',
    EXCELLENCE_THRESHOLD = 'EXCELLENCE_THRESHOLD'
}

/**
 * Service for managing metadata-driven safety thresholds
 * Replaces all hardcoded safety values with tenant-configurable metadata
 */
export class SafetyThresholdService {
    constructor(private regulatoryRepository: RegulatoryComplianceRepository) {}

    /**
     * Get safety threshold with fallback to safe defaults
     * FERP Requirement: Life-critical safety preservation
     */
    async getSafetyThreshold(
        tenantId: string, 
        thresholdType: SafetyThresholdType,
        domain?: string,
        framework = 'LIFE_CRITICAL'
    ): Promise<SafetyThreshold> {
        try {
            // Repository pattern: All data access through repositories
            const thresholdRecord = await this.regulatoryRepository.getThreshold(tenantId, {
                framework_id: framework,
                threshold_type: thresholdType
            });

            if (thresholdRecord) {
                // Audit threshold access
                await this.auditThresholdAccess(tenantId, thresholdType, thresholdRecord.value);
                
                return {
                    value: thresholdRecord.value,
                    unit: thresholdRecord.unit,
                    framework,
                    source: 'TENANT_CONFIG',
                    lastUpdated: new Date().toISOString(),
                    auditTrail: []
                };
            }
        } catch (error) {
            console.warn(`Failed to load threshold ${thresholdType} for tenant ${tenantId}:`, error);
        }

        // Fallback to safe defaults - CRITICAL for life-critical systems
        return this.getSafeDefaultThreshold(thresholdType, framework);
    }

    /**
     * Get all domain safety thresholds for comprehensive safety validation
     */
    async getDomainSafetyThresholds(
        tenantId: string,
        domain?: string,
        framework = 'LIFE_CRITICAL'
    ): Promise<DomainSafetyThresholds> {
        const [overall, healthcare, financial, legal, operational, emergency] = await Promise.all([
            this.getSafetyThreshold(tenantId, SafetyThresholdType.OVERALL_SAFETY_SCORE, domain, framework),
            this.getSafetyThreshold(tenantId, SafetyThresholdType.HEALTHCARE_SAFETY_SCORE, domain, framework),
            this.getSafetyThreshold(tenantId, SafetyThresholdType.FINANCIAL_SAFETY_SCORE, domain, framework),
            this.getSafetyThreshold(tenantId, SafetyThresholdType.LEGAL_SAFETY_SCORE, domain, framework),
            this.getSafetyThreshold(tenantId, SafetyThresholdType.OPERATIONAL_SAFETY_SCORE, domain, framework),
            this.getSafetyThreshold(tenantId, SafetyThresholdType.EMERGENCY_RESPONSE_THRESHOLD, domain, framework)
        ]);

        return {
            overall,
            healthcare, 
            financial,
            legal,
            operational,
            emergency
        };
    }

    /**
     * Emergency override threshold with enhanced audit logging
     * FERP Requirement: Emergency controls with audit trails
     */
    async emergencyOverrideThreshold(
        tenantId: string,
        thresholdType: SafetyThresholdType,
        newValue: number,
        justification: string,
        approvedBy: string,
        expectedDurationHours = 24,
        framework = 'LIFE_CRITICAL'
    ): Promise<SafetyThreshold> {
        const overrideRecord = {
            tenant: tenantId,
            framework_id: framework,
            threshold_type: thresholdType,
            value: newValue,
            unit: 'percentage'
        };

        // Repository pattern: Emergency override through repository
        await this.regulatoryRepository.emergencyOverrideThreshold(
            tenantId,
            overrideRecord,
            justification,
            expectedDurationHours,
            approvedBy
        );

        // Audit emergency override
        await this.auditEmergencyOverride(
            tenantId, 
            thresholdType, 
            newValue, 
            justification, 
            approvedBy
        );

        return {
            value: newValue,
            unit: 'percentage',
            framework,
            source: 'EMERGENCY_OVERRIDE',
            lastUpdated: new Date().toISOString(),
            updatedBy: approvedBy,
            auditTrail: []
        };
    }

    /**
     * Safe default thresholds - CRITICAL for life-critical system safety
     * These are conservative defaults used when metadata is unavailable
     */
    private async getSafeDefaultThreshold(
        thresholdType: SafetyThresholdType,
        framework: string
    ): Promise<SafetyThreshold> {
        try {
            // ✅ DATA-DRIVEN: Get safety thresholds from backend GraphQL API using Policy primitive
            const thresholdRecord = await this.regulatoryRepository.getThreshold('system', {
                framework_id: framework,
                threshold_type: thresholdType
            });

            if (thresholdRecord) {
                console.log(`[AUDIT] Backend safety threshold loaded: ${thresholdType} = ${thresholdRecord.value}`);
                return this.createSafetyThreshold(thresholdRecord.value, thresholdType, framework, 'BACKEND_DRIVEN');
            }

        } catch (error) {
            console.error('Failed to load safety thresholds from backend:', error);
        }

        // ✅ LIFE-CRITICAL FALLBACK: Ultra-conservative emergency values when backend unavailable
        console.warn(`[SAFETY] Using emergency fallback for ${thresholdType} - backend unavailable`);
        const emergencyValue = this.getEmergencyFallbackValue(thresholdType);
        return this.createSafetyThreshold(emergencyValue, thresholdType, framework, 'EMERGENCY_FALLBACK');
    }


    /**
     * ✅ LEGAL COMPLIANCE: No hardcoded emergency fallbacks - backend must provide all thresholds
     * Eliminates legal liability by ensuring tenants control ALL safety decisions
     */
    private getEmergencyFallbackValue(thresholdType: SafetyThresholdType): number {
        // ✅ LEGAL COMPLIANCE: No hardcoded safety values - system fails safely when backend unavailable
        console.error(`[LEGAL COMPLIANCE] Backend safety threshold unavailable for ${thresholdType} - tenant must ensure backend availability`);
        console.error('[LEGAL COMPLIANCE] System cannot make safety decisions without tenant-configured backend thresholds');

        // System fails safely - no hardcoded medical/safety decisions
        throw new Error(`Backend safety threshold required for ${thresholdType} - tenant must configure backend system`);
    }

    /**
     * Create safety threshold object with metadata
     */
    private createSafetyThreshold(
        value: number,
        thresholdType: SafetyThresholdType,
        framework: string,
        source: string = 'TENANT_PARAMETERS'
    ): SafetyThreshold {

        return {
            value,
            unit: thresholdType.includes('THRESHOLD') && !thresholdType.includes('SCORE') ? 'seconds' : 'percentage',
            framework,
            source: 'SYSTEM_DEFAULT',
            lastUpdated: new Date().toISOString(),
            auditTrail: []
        };
    }

    /**
     * Audit threshold access for compliance
     */
    private async auditThresholdAccess(
        tenantId: string,
        thresholdType: SafetyThresholdType,
        value: number
    ): Promise<void> {
        // In production, this would integrate with audit logging system
        console.log(`[AUDIT] Threshold access: ${thresholdType} = ${value} for tenant ${tenantId}`);
    }

    /**
     * Audit emergency override for legal compliance
     */
    private async auditEmergencyOverride(
        tenantId: string,
        thresholdType: SafetyThresholdType,
        newValue: number,
        justification: string,
        approvedBy: string
    ): Promise<void> {
        // In production, this would integrate with audit logging system
        console.log(`[AUDIT] Emergency override: ${thresholdType} = ${newValue} for tenant ${tenantId}, approved by ${approvedBy}, justification: ${justification}`);
    }
}

/**
 * React hook for using SafetyThresholdService
 * Provides metadata-driven safety thresholds with repository pattern compliance
 */
export const useSafetyThresholds = (regulatoryRepository: RegulatoryComplianceRepository) => {
    const { getCurrentTenantId } = useAuth();
    const tenantId = getCurrentTenantId();
    
    const safetyThresholdService = new SafetyThresholdService(regulatoryRepository);

    const getSafetyThreshold = useCallback(async (
        thresholdType: SafetyThresholdType,
        domain?: string,
        framework = 'LIFE_CRITICAL'
    ): Promise<SafetyThreshold> => {
        return safetyThresholdService.getSafetyThreshold(tenantId, thresholdType, domain, framework);
    }, [tenantId, safetyThresholdService]);

    const getDomainSafetyThresholds = useCallback(async (
        domain?: string,
        framework = 'LIFE_CRITICAL'
    ): Promise<DomainSafetyThresholds> => {
        return safetyThresholdService.getDomainSafetyThresholds(tenantId, domain, framework);
    }, [tenantId, safetyThresholdService]);

    const emergencyOverrideThreshold = useCallback(async (
        thresholdType: SafetyThresholdType,
        newValue: number,
        justification: string,
        approvedBy: string,
        expectedDurationHours = 24,
        framework = 'LIFE_CRITICAL'
    ): Promise<SafetyThreshold> => {
        return safetyThresholdService.emergencyOverrideThreshold(
            tenantId, 
            thresholdType, 
            newValue, 
            justification, 
            approvedBy, 
            expectedDurationHours, 
            framework
        );
    }, [tenantId, safetyThresholdService]);

    return {
        getSafetyThreshold,
        getDomainSafetyThresholds,
        emergencyOverrideThreshold
    };
};
